import React, {useState, useEffect, MutableRefObject} from 'react';
import wrap from 'wraptile';
import {curry} from 'ramda';
import {nanoid} from 'nanoid';
import CircularProgress from '@material-ui/core/CircularProgress';
import {Genre} from '../../types';
import {
    MetadataGeneral,
    MetadataLocalized,
    MetadataTerritorial,
    Role,
    SetQcMetadataType,
    MetadataContentType,
    CategoryItem,
    Descriptor,
    VideoTypeData,
} from './ContentItemViewMetadataTypes';
import QCMetadataGeneral from './ContentItemViewMetadataGeneral';
import QCMetadataLocalized from './ContentItemViewMetadataLocalized';
import QCMetadataTerritorial from './ContentItemViewMetadataTerritorial';
import './ContentItemViewMetadata.scss';

export interface ContentItemViewMetadataContainerProps {
    categories: CategoryItem[] | null;
    isLoadingCategories: boolean;
    setMetadataCategories: any;
    isLoadingGenres: boolean;
    genres: Genre[];
    setQcGenres: any;
    setVideoData: (id: number) => void;
    general: MetadataGeneral;
    localized: MetadataLocalized;
    territorial: MetadataTerritorial;
    isLoading: boolean;
    id: number;
    allotment: number | null;
    reloadRef: MutableRefObject<boolean>;
    ratingDescriptors: Descriptor[];
    setQcDescriptors: any;
    isLoadingDescriptors: boolean;
    videoData: {
        video: VideoTypeData;
    };
    jobDeliveryDate: string;
}

export const handleFetchQcMetadata = wrap(
    (setQcMetadata: SetQcMetadataType, id: number, isLoading: boolean) => {
        if (isLoading) setQcMetadata(id);
    }
);

export const mapPartItem = curry(
    (label: string, value: string): JSX.Element => {
        return (
            <div className="part-wrapper" key={nanoid()}>
                <div className="part-label">{label}</div>
                <div className="part-value">{value}</div>
            </div>
        );
    }
);

export const getPartItems = (label: string, values: string[]): JSX.Element[] => {
    return values.map(mapPartItem(label));
};

export const mapToRolesObject = curry((role: string, {id, label}: Role) => {
    return {
        id,
        role,
        name: label,
    };
});

export const handleConfirmNavigationClick = wrap(
    (
        setModalVisible: (value: boolean) => void,
        setConfirmedNavigation: (value: boolean) => void
    ) => {
        setModalVisible(false);
        setConfirmedNavigation(true);
    }
);

export const handleCloseModal = wrap((setModalVisible: (value: boolean) => void) => {
    setModalVisible(false);
});

export const reloadEffect = wrap(
    (lastLocation: Location, reloadRef: MutableRefObject<boolean>) => {
        if (lastLocation) {
            reloadRef.current = true;
            window.location.replace(lastLocation.pathname);
        }
    }
);

export const handleFetchGenres = wrap((isLoadingGenres: boolean, setQcGenres: any) => {
    if (isLoadingGenres) {
        setQcGenres();
    }
});

export const handleFetchDescriptors = wrap(
    (isLoadingDescriptors: boolean, setQcDescriptors: any) => {
        if (isLoadingDescriptors) {
            setQcDescriptors();
        }
    }
);

export const handleFetchCategories = wrap(
    (isLoadingCategories: boolean, setMetadataCategories: any) => {
        if (isLoadingCategories) {
            setMetadataCategories();
        }
    }
);

export const getMetadataContentType = (
    contentType: string | undefined
): MetadataContentType => {
    switch (contentType) {
        case 'web_originals':
            return MetadataContentType.WebOriginals;
        case 'episode':
            return MetadataContentType.Episode;
        case 'film':
            return MetadataContentType.Film;
        case 'series':
            return MetadataContentType.Series;
        default:
            return MetadataContentType.WebOriginals;
    }
};

export const mapActors = mapToRolesObject('actor');
export const mapDirectors = mapToRolesObject('director');
export const mapProducers = mapToRolesObject('producer');
export const mapComposers = mapToRolesObject('composer');
export const mapWriters = mapToRolesObject('writer');

function ContentItemViewMetadataContainer(props: ContentItemViewMetadataContainerProps) {
    const {
        isLoadingGenres,
        genres,
        setQcGenres,
        reloadRef,
        isLoading,
        general,
        localized,
        territorial,
        id,
        allotment,
        categories,
        isLoadingCategories,
        setMetadataCategories,
        ratingDescriptors,
        setQcDescriptors,
        isLoadingDescriptors,
        videoData,
        jobDeliveryDate,
    } = props;
    const {roles, contentType} = general;
    const [lastLocation] = useState<Location | null>(null);

    useEffect(reloadEffect(lastLocation, reloadRef), []);

    useEffect(handleFetchGenres(isLoadingGenres, setQcGenres), []);

    useEffect(handleFetchDescriptors(isLoadingDescriptors, setQcDescriptors), []);

    useEffect(handleFetchCategories(isLoadingCategories, setMetadataCategories), []);

    const metadataContentType = getMetadataContentType(contentType);

    const {actors, directors, producers, composers, writers} = roles;

    const actorsInitialValues = actors.map(mapActors);
    const directorsInitialValues = directors.map(mapDirectors);
    const producersInitialValues = producers.map(mapProducers);
    const composersInitialValues = composers.map(mapComposers);
    const writersInitialValues = writers.map(mapWriters);

    if (isLoading) {
        return <CircularProgress />;
    }

    const widthLocalized =
        contentType !== 'series' ? `localized-width-small` : `localized-width`;

    return (
        <div className="qc-metadata-view">
            <div className="qc-metadata-view__general">
                <QCMetadataGeneral
                    jobDeliveryDate={jobDeliveryDate}
                    allotment={allotment}
                    genres={genres}
                    videoData={videoData}
                    metadataContentType={metadataContentType}
                    general={general}
                    taskId={id}
                    actorsInitialValues={actorsInitialValues}
                    directorsInitialValues={directorsInitialValues}
                    producersInitialValues={producersInitialValues}
                    composersInitialValues={composersInitialValues}
                    writersInitialValues={writersInitialValues}
                />
            </div>
            <div
                className={`qc-metadata-view__localized qc-metadata-view__${widthLocalized}`}
            >
                <QCMetadataLocalized
                    jobDeliveryDate={jobDeliveryDate}
                    categories={categories}
                    metadataContentType={metadataContentType}
                    localized={localized}
                />
            </div>
            {contentType !== 'series' ? (
                <div className="qc-metadata-view__territorial">
                    <QCMetadataTerritorial
                        jobDeliveryDate={jobDeliveryDate}
                        territorial={territorial}
                        taskId={id}
                        ratingDescriptors={ratingDescriptors}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default React.memo(ContentItemViewMetadataContainer);
